import React from 'react';
import { Trophy, Target, ArrowRight, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { CommissionReportButton } from './CommissionReportButton';
import { OfficerData } from '../data/officers';
// Salesforce commission report — replace with the real report URL
const COMMISSION_REPORT_URL = 'https://your-org.lightning.force.com/lightning/r/Report/00O000000000000/view';
interface RankStatusHeroProps {
  officer: OfficerData;
}
export function RankStatusHero({
  officer
}: RankStatusHeroProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6" data-id="element-808" data-mp="2fh9qhisq61f1">
      <div className="bg-[#0a1e5e] px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2" data-id="element-809" data-mp="20lk8dvqnu8fl">
        <div className="flex items-center gap-3 text-white min-w-0" data-id="element-810" data-mp="1wjgn6fe6vph4">
          <Trophy className="w-6 h-6 text-yellow-400 shrink-0" data-id="element-811" data-mp="1wmcl43q1ee5k" />
          <h2 className="text-lg font-bold truncate" data-id="element-812" data-mp="24weet35bv9av">Career Progression</h2>
        </div>
        <div className="text-blue-200 text-xs sm:text-sm font-medium" data-id="element-813" data-mp="29ph7za670gqj">
          Your Path. Your Production. Your Future.
        </div>
      </div>

      <div className="p-4 sm:p-6 flex flex-col lg:flex-row gap-6 items-stretch" data-id="element-814" data-mp="1zyw2r3w0kdyn">
        {/* Current Status */}
        <div className="flex-1 w-full bg-blue-50 rounded-xl p-5 sm:p-6 border border-blue-100 relative overflow-hidden min-w-0" data-id="element-815" data-mp="2noqfzmk8w4jf">
          <div className="absolute top-0 right-0 w-32 h-32 bg-blue-100 rounded-full -mr-10 -mt-10 opacity-50 pointer-events-none" data-id="element-816" data-mp="2smicktvu4ety"></div>
          <div className="relative" data-id="element-817" data-mp="2epv5vze2yaau">
            <p className="text-sm font-bold text-blue-600 uppercase tracking-wider mb-1" data-id="element-818" data-mp="2iouim661zbcu">
              Current Rank
            </p>
            <h3 className="text-2xl sm:text-3xl font-extrabold text-[#0a1e5e] mb-4 leading-tight break-words" data-id="element-819" data-mp="137u0ktr5oywg">
              {officer.currentRank}
            </h3>

            <CommissionReportButton reportUrl={COMMISSION_REPORT_URL} data-id="element-820" data-mp="39xf94btv78eo" />
          </div>
        </div>

        <div className="hidden lg:flex items-center justify-center text-gray-300 shrink-0" data-id="element-821" data-mp="34x9hgvnlhlwl">
          <ArrowRight className="w-10 h-10" data-id="element-822" data-mp="306knn9og0cua" />
        </div>

        {/* Next Target */}
        <div className="flex-[2] w-full bg-white rounded-xl p-5 sm:p-6 border border-gray-200 shadow-sm min-w-0" data-id="element-823" data-mp="2v89e702284cl">
          <div className="mb-5" data-id="element-824" data-mp="2r6xner7g9d2t">
            <p className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-825" data-mp="2r7z8zpm3dhys">
              Next Target
            </p>
            <h3 className="text-xl sm:text-2xl font-extrabold text-[#0a1e5e] flex items-center gap-2 leading-tight" data-id="element-826" data-mp="2m9nxzbk291ez">
              <span className="break-words" data-id="element-827" data-mp="2c9djlnmahny8">{officer.nextRank}</span>
              <Target className="w-5 h-5 text-blue-600 shrink-0" data-id="element-828" data-mp="2cb88fd0om07l" />
            </h3>
          </div>

          <div className="grid md:grid-cols-2 gap-5 md:gap-6 relative" data-id="element-829" data-mp="fer7mmh86650">
            {/* OR badge - placed between grid columns on md+ */}
            <div className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 bg-white border border-gray-200 text-[10px] font-bold px-2 py-0.5 rounded-full text-gray-500 shadow-sm pointer-events-none" data-id="element-830" data-mp="izavu0ygbizv">
              OR
            </div>

            {/* Path 1: Tenure */}
            <div className="min-w-0" data-id="element-831" data-mp="wqpiqivhc4c7">
              <div className="flex justify-between gap-2 text-sm mb-2" data-id="element-832" data-mp="wpo0nug1qhkz">
                <span className="font-semibold text-gray-700" data-id="element-833" data-mp="ymof7ji94a">
                  Path 1: Tenure
                </span>
                <span className="text-gray-500 shrink-0 text-xs sm:text-sm" data-id="element-834" data-mp="420moy9a6p7b">
                  {officer.tenureProgress}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1.5 overflow-hidden" data-id="element-835" data-mp="e07qsgih1crw">
                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                width: `${officer.tenurePercent}%`
              }} data-id="element-836" data-mp="1kg9zqw82oriu"></div>
              </div>
              <p className="text-xs text-gray-500" data-id="element-837" data-mp="2yctjkxb20hpg">
                {officer.tenurePercent >= 100 ? 'Tenure requirement met' : 'Toward tenure requirement'}
              </p>
            </div>

            {/* Path 2: Units */}
            <div className="min-w-0" data-id="element-838" data-mp="32dn5jl4xrmfp">
              <div className="flex justify-between gap-2 text-sm mb-2 flex-wrap" data-id="element-839" data-mp="32cltpeg3ivwx">
                <span className="font-semibold text-[#0a1e5e] flex items-center gap-1.5 flex-wrap" data-id="element-840" data-mp="3h9un0doym9ra">
                  Path 2: Units
                  <span className="bg-green-100 text-green-700 text-[10px] px-1.5 py-0.5 rounded uppercase font-bold whitespace-nowrap" data-id="element-841" data-mp="3cbb0ab2jktxv">
                    Closer
                  </span>
                </span>
                <span className="text-[#0a1e5e] font-bold shrink-0 text-xs sm:text-sm" data-id="element-842" data-mp="3m3h2edmicrqs">
                  {officer.unitsForNextRank} / {officer.unitsNeeded} units
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1.5 overflow-hidden" data-id="element-843" data-mp="3uzy6wy0d9rdk">
                <div className="bg-green-500 h-2.5 rounded-full" style={{
                width: `${officer.unitsPercent}%`
              }} data-id="element-844" data-mp="igdkf4y3cfpr"></div>
              </div>
              <p className="text-xs text-gray-600 font-medium" data-id="element-845" data-mp="dj3m0zsq4tps">
                In any 4-month rolling period
              </p>
            </div>
          </div>

          <div className="mt-5 bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-start gap-3" data-id="element-846" data-mp="3vw1u5tzo6sv1">
            <CheckCircle2 className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" data-id="element-847" data-mp="4krzb8uzag2m" />
            <div className="min-w-0" data-id="element-848" data-mp="3m0qcb2syt5j2">
              <p className="text-sm font-semibold text-[#0a1e5e]" data-id="element-849" data-mp="3qy9pdzdxfc9f">
                Action Plan
              </p>
              <p className="text-sm text-gray-700 leading-snug" data-id="element-850" data-mp="3cyqvoblv43gn">
                {officer.actionPlan}
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}